import React, { useRef, useCallback, useEffect, useMemo, Component, useContext } from 'react';
import PropTypes from 'prop-types';

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _inheritsLoose(t, o) {
  t.prototype = Object.create(o.prototype), t.prototype.constructor = t, _setPrototypeOf(t, o);
}
function _setPrototypeOf(t, e) {
  return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) {
    return t.__proto__ = e, t;
  }, _setPrototypeOf(t, e);
}

/**
 * This is the single source of truth for analytics events
 * Each event is assigned an object consisting of two properties
 * @property {string} name - the name of the analytics event
 * @property {array of strings} requiredFields- the name of the properties that must be specified
 * when recording the analytics event
 */
var sharedEvents = {
  productOpened: {
    name: 'Product Opened',
    requiredFields: ['name']
  },
  productLeft: {
    name: 'Product Left',
    requiredFields: ['name']
  },
  itemClicked: {
    name: "Item Clicked",
    requiredFields: ['prefix']
  },
  pageViewed: {
    name: 'Page Viewed',
    requiredFields: ['prefix']
  },
  accountCreated: {
    name: 'Prospect Account Created',
    requiredFields: ['accountRole']
  },
  prospectSignedUp: {
    name: 'Prospect Signed Up'
  },
  prospectAccountVerified: {
    name: 'Prospect Account Verified',
    requiredFields: []
  }
};

var noop = function noop() {};
var AnalyticsContext = /*#__PURE__*/React.createContext({
  events: {
    pageViewed: noop
  },
  trackEvent: noop
});

/**
 * Checks that the list of requiredFields is included in properties object
 * @param properties
 * @param requiredFields
 * @returns {Array.<*>}
 */
function getMissingFields(properties, requiredFields) {
  if (requiredFields === void 0) {
    requiredFields = [];
  }
  return requiredFields.filter(function (field) {
    return properties[field] === undefined || properties[field] === null;
  });
}
/**
 * String interpolation to return error message when an event is dispatched
 * with missing fields required by the event spec.
 * @param eventName
 * @param missingFields
 * @returns {string}
 */
function getErrorMessage(eventName, missingFields) {
  return "Event " + eventName + " is missing required fields " + JSON.stringify(missingFields);
}
/**
 * Function looks at eventSpec (object that defines an event, see productSpecification.js for example)
 * and ensures that all required fields are passed before dispatching the event. If fields are missing
 * then an error is thrown.
 * @param dispatchHandler
 * @param eventSpec
 * @returns {dispatchEvent}
 */
function getDispatchForEvent(dispatchHandler, eventSpec) {
  function dispatchEvent(properties) {
    if (properties === void 0) {
      properties = {};
    }
    var missingFields = getMissingFields(properties, eventSpec.requiredFields);
    if (missingFields.length > 0) {
      console.error(getErrorMessage(eventSpec.name, missingFields));
    }
    var prefix = eventSpec.requiredFields && eventSpec.requiredFields.includes('prefix') ? properties.prefix + " " : '';
    var eventName = "" + prefix + eventSpec.name;
    dispatchHandler(eventName, properties);
  }
  return dispatchEvent;
}
/**
 * Iterates through a key-value object of sharedEvents
 * Creates and returns a new object with the same sharedEvent key as the key, and
 * the dispatch function as the value  after getting it from getDispatchForEvent
 */
var getEvents = function getEvents(dispatchHandler, sharedEventsSpec, productEvents) {
  var allEvents = _extends({}, sharedEventsSpec, productEvents);
  return Object.keys(allEvents).reduce(function (events, key) {
    var _extends2;
    var eventSpec = allEvents[key];
    return _extends({}, events, (_extends2 = {}, _extends2[key] = getDispatchForEvent(dispatchHandler, eventSpec), _extends2));
  }, {});
};

/**
 * This class wraps a component and exposes various Analytics related methods
 * to the context api which is then available to all nested components.
 */
var AnalyticsProvider = function AnalyticsProvider(_ref) {
  var adaptors = _ref.adaptors,
    productEvents = _ref.productEvents,
    user = _ref.user,
    children = _ref.children;
  var globalPropertiesRef = useRef({});
  var notFirstRenderRef = useRef(false);
  /**
   * Calls the adaptor setUser method to set the user based on the UUID
   * @param {object} user - user object containing id and account role
   */
  var setUser = useCallback(function (userObj) {
    adaptors.forEach(function (adaptor) {
      if (!adaptor.setUser) return;
      adaptor.setUser({
        id: userObj.id,
        accountRole: userObj.accountRole
      });
    });
  }, [adaptors]);
  var unsetUser = useCallback(function () {
    adaptors.forEach(function (adaptor) {
      if (!adaptor.reset) return;
      adaptor.reset();
    });
  }, [adaptors]);
  useEffect(function () {
    if (user) {
      setUser({
        id: user.id,
        accountRole: user.accountRole
      });
    } else if (notFirstRenderRef.current) {
      unsetUser();
    }
    notFirstRenderRef.current = true;
  }, [setUser, unsetUser, user]);
  /**
   * Sets a key-value pair that should be sent as a property with every analytics event.
   * @param {string} key - the property key to be sent with every event.
   * @param {string} value - the property value corresponding to the key to be sent with every event.
   */
  var setAnalyticsValue = useCallback(function (key, value) {
    globalPropertiesRef.current[key] = value;
  }, []);
  var unsetAnalyticsValue = useCallback(function (key) {
    delete globalPropertiesRef.current[key];
  }, []);
  /**
   * Calls the adaptor alias method to alias a visitor to a user id, alias is only called once in the lifecycle of a user
   * @param {object} user - user object containing id and account role
   * @returns {Promise} - promise object with the result of calling all adaptors aliasVisitor methods
   */
  var aliasVisitor = useCallback(function (userObj) {
    return Promise.all(adaptors.map(function (adaptor) {
      if (!adaptor.aliasVisitor) return;
      return adaptor.aliasVisitor({
        id: userObj.id,
        accountRole: userObj.accountRole
      });
    }));
  }, [adaptors]);
  /**
   * Calls the adaptor method to record an analytics event with properties.
   * This is the only mandatory adaptor method.
   * @param {string} name - name of the event
   * @param {object} properties - object of key-value pairs to send with the event
   */
  var trackEvent = useCallback(function (name, properties) {
    adaptors.forEach(function (adaptor) {
      adaptor.trackEvent(name, _extends({}, properties, globalPropertiesRef.current));
    });
  }, [adaptors]);
  var events = useMemo(function () {
    return getEvents(trackEvent, sharedEvents, productEvents);
  }, [productEvents, trackEvent]);
  var analytics = useMemo(function () {
    return {
      setAnalyticsValue: setAnalyticsValue,
      unsetAnalyticsValue: unsetAnalyticsValue,
      trackEvent: trackEvent,
      setUser: setUser,
      unsetUser: unsetUser,
      aliasVisitor: aliasVisitor,
      events: events
    };
  }, [aliasVisitor, events, setAnalyticsValue, setUser, trackEvent, unsetAnalyticsValue, unsetUser]);
  return React.createElement(AnalyticsContext.Provider, {
    value: analytics
  }, children);
};
AnalyticsProvider.defaultProps = {
  adaptors: []
};

/**
 * Component that sets and unsets analytics properties (through the AnalyticsProvider)
 * based on mount and dismount of the component. AnalyticsProvider must be wrapped further
 * up the tree for AnalyticsValue to be utilised successfully.
 */
var AnalyticsValue = /*#__PURE__*/function (_Component) {
  function AnalyticsValue() {
    return _Component.apply(this, arguments) || this;
  }
  _inheritsLoose(AnalyticsValue, _Component);
  var _proto = AnalyticsValue.prototype;
  /**
   * On component mount, call the appropriate context function set by AnalyticProvider
   * further up the tree.
   */
  _proto.componentDidMount = function componentDidMount() {
    this.context.setAnalyticsValue(this.props.name, this.props.value);
  };
  _proto.componentWillUnmount = function componentWillUnmount() {
    this.context.unsetAnalyticsValue(this.props.name);
  };
  _proto.render = function render() {
    return null;
  };
  return AnalyticsValue;
}(Component);
AnalyticsValue.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.any
};
AnalyticsValue.defaultProps = {
  value: ''
};
AnalyticsValue.contextType = AnalyticsContext;

function useAnalytics() {
  var context = useContext(AnalyticsContext);
  var analytics = {
    setUser: context.setUser,
    unsetUser: context.unsetUser,
    events: context.events,
    trackEvent: context.trackEvent,
    aliasVisitor: context.aliasVisitor,
    setAnalyticsValue: context.setAnalyticsValue,
    unsetAnalyticsValue: context.unsetAnalyticsValue
  };
  return analytics;
}

function usePageView(pageName) {
  var _useAnalytics = useAnalytics(),
    pageViewed = _useAnalytics.events.pageViewed;
  useEffect(function () {
    pageViewed({
      prefix: pageName
    });
  }, [pageName, pageViewed]);
}

export { AnalyticsProvider, AnalyticsValue, useAnalytics, usePageView };
//# sourceMappingURL=tracking.esm.js.map
